import React from 'react';
import { FileText, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { salonInfo } from '../data/mock';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-[#EBF4F6] via-white to-[#EBF4F6]/50 overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Badge className="mb-6 bg-[#3D7A8C]/10 text-[#3D7A8C] border-0 px-5 py-2 text-sm font-medium">
            Legal Information
          </Badge>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-gray-900 tracking-tight">
            Terms of Service
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Introduction */}
        <Card className="border-2 border-gray-100 rounded-2xl mb-8">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-[#3D7A8C]" />
              </div>
              <div>
                <h2 className="font-serif text-2xl font-bold text-gray-900 mb-3">Agreement to Terms</h2>
                <p className="text-gray-700 leading-relaxed">
                  By accessing and using the services provided by <strong>{salonInfo.registeredName}</strong>, operating under the brand name &ldquo;<strong>{salonInfo.brandName}</strong>&rdquo;, you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access our services.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card className="border-2 border-gray-100 rounded-2xl mb-8">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-[#3D7A8C]" />
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">Services Provided</h2>
                <p className="text-gray-700 mb-4">
                  {salonInfo.brandName} provides beauty and wellness services including, but not limited to:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Hair styling and treatments</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Nail care services</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Makeup application</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Spa and skincare treatments</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Other beauty services as advertised</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Appointments & Cancellations */}
        <Card className="border-2 border-gray-100 rounded-2xl mb-8">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-[#3D7A8C]" />
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">Appointments & Cancellations</h2>
                
                <h3 className="font-semibold text-lg text-gray-900 mb-3">Booking</h3>
                <p className="text-gray-700 mb-4">
                  Appointments can be made by phone or in person at our salon. All appointments are subject to availability.
                </p>

                <h3 className="font-semibold text-lg text-gray-900 mb-3">Cancellation Policy</h3>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Please provide at least 24 hours notice for cancellations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Late cancellations may be subject to a cancellation fee</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">No-shows may be charged the full service price</span>
                  </li>
                </ul>

                <h3 className="font-semibold text-lg text-gray-900 mb-3">Late Arrivals</h3>
                <p className="text-gray-700">
                  If you arrive late for your appointment, we will do our best to accommodate you. However, your service time may be shortened to avoid delays for other clients.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment */}
        <Card className="border-2 border-gray-100 rounded-2xl mb-8">
          <CardContent className="p-8">
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">Payment Terms</h2>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Payment is due at the time of service</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">We accept cash, credit cards, and debit cards</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Prices are subject to change without notice</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Gratuity is not included in service prices</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Liability */}
        <Card className="border-2 border-gray-100 rounded-2xl mb-8">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center flex-shrink-0">
                <XCircle className="w-6 h-6 text-[#3D7A8C]" />
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
                <p className="text-gray-700 mb-4">
                  While we strive to provide the highest quality services, {salonInfo.registeredName} (operating as {salonInfo.brandName}) is not liable for:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Allergic reactions to products (please inform us of any allergies beforehand)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Results from treatments if proper aftercare instructions are not followed</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#3D7A8C] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Loss or damage to personal items brought to our facility</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Changes to Terms */}
        <Card className="border-2 border-gray-100 rounded-2xl mb-8">
          <CardContent className="p-8">
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify or replace these Terms of Service at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after any changes constitutes acceptance of the new terms.
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="border-2 border-[#3D7A8C]/30 rounded-2xl bg-gradient-to-br from-[#EBF4F6] to-[#D7E9ED]/50">
          <CardContent className="p-8 text-center">
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">Questions About These Terms?</h2>
            <p className="text-gray-700 mb-6">
              If you have any questions or concerns about these Terms of Service, please contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Brand:</strong> {salonInfo.brandName}</p>
              <p><strong>Registered Company:</strong> {salonInfo.registeredName}</p>
              <p><strong>Email:</strong> <a href={`mailto:${salonInfo.email}`} className="text-[#3D7A8C] hover:text-[#356A7A]">{salonInfo.email}</a></p>
              <p><strong>Phone:</strong> <a href={`tel:${salonInfo.phone}`} className="text-[#3D7A8C] hover:text-[#356A7A]">{salonInfo.phone}</a></p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
